import React, { useState, useCallback, useRef, useEffect, ChangeEventHandler, ChangeEvent, useMemo, memo } from "react";
import { Editor } from '@hugerte/hugerte-react';
import FloatBoxDialog from "./Plugins/FloatBoxDialog";
import { useI18n } from "../../utils/lni18n";
import { Bookmark, EditorEvent, Editor as HugeRteEditor } from 'hugerte';
import { PUBLIC_URL } from "../../utils/constants";
import imagePickerService from "../ImagePicker/ImagePickerService";
import { align } from "../ImagePicker/ImagePicker_types";
import ImagePickerDialog from "../ImagePicker/ImagePickerDialog";
import ImageAlignDialog from "../ImagePicker/ImageAlignDialog";
import { TextEditTypes } from "src/coursepart/domain/SavePartTextDto";
import { useDialog } from "../Modalservice/Dialogservice";
import { imageEndPoints, PickedImageData } from "../ImagePicker/ImagePicker";
import ImageDrop from "../ImagePicker/ImageDrop";
import DOMPurify from 'dompurify';
import { debouncer } from "src/utils/debouncer";

export type EditorProps = {
    className?: string,
    initialValue?: string,
    onChange: ChangeEventHandler<HTMLTextAreaElement>,
    onClick?: React.MouseEventHandler<HTMLDivElement>,
    onBlur?: any,
    name: string | TextEditTypes,
    editorSettings?: any,
    onSetup?: Function,
    value?: string,
    invalid?: boolean,
    readOnly?: boolean
    onFocus?: (el: HTMLElement) => void
};

const HtmlTextEditor = memo(function HtmlTextEditor(props: EditorProps) {

    const editorWrapper = useRef<any>(undefined);
    const fileOptions = {
        fileExtensions: "bmp,gif,jpg,png,tif,tiff,jpeg,webp",
        maxsize: "10mb",
        mimetypes: "image/jpeg,image/png,image/gif,image/tiff,image/bmp,image/webp"
    };

    const { onFocus, readOnly, invalid, initialValue, name, editorSettings, onClick, onSetup, onBlur, onChange } = props;

    const dialogPortal = useDialog();
    const [dropFiles, setDropFilesState] = useState<File[] | FileList | null>(null);
    const triggerOnChangeDebouncerRef = useRef<debouncer | null>(null);

    const menuElementId = useMemo(() => "menu_" + Math.floor(Math.random() * 100000), []);

    const { currentLanguage, languageService: t } = useI18n();

    // NOTE: to make this works with our language name I have changed file the sv_SE.js to sv.js
    //       and in that file 'hugerte.addI18n('sv_SE'...'  to 'hugerte.addI18n('sv' ...'
    //       (the bundled langs in public/hugerte/langs call the `hugerte` global)
    let lang = currentLanguage.currentLang.Short;

    const editorRef = useRef<HugeRteEditor | null>(null);

    const editor = () => {
        return editorRef.current;
    }
    const setEditor = (ed: HugeRteEditor) => {
        editorRef.current = ed;
    }

    const bookmarkRef = useRef<Bookmark | null>(null);
    const bookmark = () => {
        return bookmarkRef.current;
    }
    const setBookmark = (m: Bookmark | null) => {
        bookmarkRef.current = m;
    }

    const setDropFiles = (files: File[] | FileList | null) => {
        const ed = editor();
        setDropFilesState(files);
        if (files !== null && ed !== null) {
            setBookmark(ed.selection.getBookmark());
        }

    };

    const triggerOnChange = useCallback((html: string) => {

        if (editorRef.current === null) return;


        const event = {
            target: {
                value: html,
                name: name,
                type: "textarea"
            },
            currentTarget: {
                value: html,
                name: name,
                type: "textarea"
            }
        } as ChangeEvent<HTMLTextAreaElement>;

        if (onChange) onChange(event);

    }, [onChange, name]);


    useEffect(() => {
        if (triggerOnChangeDebouncerRef.current != null) {
            triggerOnChangeDebouncerRef.current.flush();
        }
        const d = new debouncer(triggerOnChange, 300);
        triggerOnChangeDebouncerRef.current = d;
        return () => {
            d.flush();
            triggerOnChangeDebouncerRef.current = null;
        };
    }, [triggerOnChange]);

    // unique id for each instance of editor
    const editorId = useMemo(() => "id_" + (new Date()).getTime() + "_", []);

    const imageDialog = useCallback((ed: HugeRteEditor) => {


        const bookMark = ed.selection.getBookmark();

        const imgXElm = ed.selection.getNode();
        const imgElm = imgXElm as HTMLImageElement;
        if (imgElm && imgElm.nodeName === "IMG") {

            let align: align = "none";
            if (imgElm.classList.contains("pull-left") === true) {
                align = "left";
            }
            if (imgElm.classList.contains("pull-right") === true) {
                align = "right";
            }
            const dataId = imgElm.attributes.getNamedItem("data-id");
            const id = dataId ? dataId.value : "";
            const src = imgElm.src;

            imagePickerService.setAlingData({
                modalOpen: true, imageAlign: align,
                imageId: id, url: src, changeOnly: true
            });


            var p = dialogPortal({
                factory: (onSubmit, onCancel) => {
                    return <ImageAlignDialog onCancel={onCancel} onSubmit={onSubmit} />
                },
                size: "md"
            });

            const editor = ed;
            p.then(res => {
                imagePickerService.setAlingData(undefined);
                if (res) {

                    imgElm.classList.remove('pull-right', 'pull-left');
                    if (res.imageAlign !== 'none') {
                        imgElm.classList.add('pull-' + res.imageAlign);
                    }
                }
                window.setTimeout(() => editor.focus(), 1000);
            }).catch(e => {
                console.error(e);
            });
        }
        else {
            imagePickerService.fetchImageData();

            var p = dialogPortal({
                factory: (onSubmit, onCancel) => {
                    return <ImagePickerDialog onCancel={onCancel} onSubmit={onSubmit} />
                },
                size: "xl"
            });

            const editor = ed;

            p.then((res: PickedImageData) => {
                imagePickerService.setModalState(false);
                imagePickerService.setAlingData(undefined);
                if (res) {
                    let className = "";
                    if (res.imageClasses !== "") {

                        className = `class="${res.imageClasses}"`;
                    }

                    ed.focus();
                    ed.selection.moveToBookmark(bookMark);
                    if (res.imageId) {
                        editor.insertContent(`<img ${className} src="${res.url}" data-id="${res.imageId}" />`)
                    }
                    else {
                        editor.insertContent(`<img ${className} src="${res.url}" />`)
                    }
                }
                window.setTimeout(() => editor.focus(), 1000);
            }).catch(e => { });


            imagePickerService.setSubmitBrooker((res) => {

            });
        }


    }, []);


    const floatBoxDialog = (ed: HugeRteEditor) => {

        const bookMark = ed.selection.getBookmark();
        
        let floatdata = { align: "none", style: "Facts", width: 400, canDelete: false };

        var box = ed.dom.getParent(ed.selection.getNode(), "div.box");
        if (box != null) {
            floatdata.width = parseInt(ed.dom.getAttrib(box, "data-width"), 10);
            floatdata.align = ed.dom.getAttrib(box, "data-align");
            floatdata.style = ed.dom.getAttrib(box, "data-type");
            floatdata.canDelete = true;
        }

        var p = dialogPortal({
            factory: (onSubmit, onCancel) => {
                return <FloatBoxDialog floatCallback={onSubmit} boxState={floatdata} />
            },
            size: "lg"
        });

        p.then(res => {
            if (res) {
                const css = `box ${res.style} ${res.align}box w${res.width}`;
                if (box) {

                    if (res.delete) {
                        ed.dom.remove(box, false)
                    }
                    else {
                        ed.dom.setAttrib(box, "data-type", res.style);
                        ed.dom.setAttrib(box, "data-align", res.align);
                        ed.dom.setAttrib(box, "data-width", res.width);
                        ed.dom.setAttrib(box, "class", css);
                    }
                }
                else {
                    ed.selection.moveToBookmark(bookMark);
                    const html = `<div class='${css}' data-type='${res.style}' data-align='${res.align}'`
                        + ` data-width='${res.width}'><p>&#160;</p></div>`;
                    ed.insertContent(html);
                }
            }
            ed.focus();
        }).catch(() => { });


    };




    const handleEditorChange = useCallback((s: string, e: HugeRteEditor) => {

        if (triggerOnChangeDebouncerRef.current === null) return;

        const html = s;
        if (e.isDirty())
            triggerOnChangeDebouncerRef.current.debounce(html);

    }, []);

    const onPaste = async (event: EditorEvent<any>, ed: HugeRteEditor) => {

        try {
            const clipboardItems = [...event.clipboardData.items] as Array<DataTransferItem>;
            const imageItem = clipboardItems.find(item => item.type.indexOf("image") !== -1);

            // no image let it go
            if (!imageItem) {
                return true;
            }

            const textItem = clipboardItems.find(item => item.type.indexOf("text/plain") !== -1);

            if (textItem) {
                const text = await getTextDataFromClipboard(textItem);
                // no enought plain text let go
                if (text && text.length > 2) {
                    return true;
                }
            }

            event.preventDefault();
            event.stopPropagation();
            const blob = await imageItem.getAsFile();
            if (!blob) return;
            const ext = imageItem.type.split("/")?.[1] || "jpg";
            const f = new File([blob], `clip.${ext}`, { type: imageItem.type });
            setDropFiles([f]);

            return true;


        } catch (err: any) {
            console.error(err.name, err.message);
        }
    }

    const getTextDataFromClipboard = (data: DataTransferItem) => {
        return new Promise<string>((resolve, reject) => {
            data.getAsString((s) => { resolve(s); });

        });
    }

    const localOnBlur = (e: any) => {
        if (editorRef.current && editorRef.current.isDirty()) {
            triggerOnChangeDebouncerRef.current?.clear();
            triggerOnChange(editorRef.current.getContent());
        }

        onBlur && onBlur(e);
    }
    const uploadImage = (ev: EditorEvent<DragEvent>, ed: HugeRteEditor) => {
        const files = ev.dataTransfer?.files;
        if (files) {
            setDropFiles(files);
        }
    }

    const handleDroppedFileReady = useCallback((id: string) => {
        const ed = editor();
        ed?.focus();
        setDropFiles(null);
        const bMark = bookmark();
        if (bMark) {
            ed?.selection.moveToBookmark(bMark);
        }

        if (id) {
            const url = imageEndPoints.GET_IMAGE_URL(id);
            ed?.insertContent(`<img src="${url}" data-id="${id}" />`)
        }
    }, [bookmark, editor]);

    const onCloseDropDialog = () => {
        setDropFiles(null);
    }

    function setImageClass(element: Element, className: string | null): void {
        const img = element as HTMLImageElement;
        img.classList.remove("float-left", "float-right");
        switch (className) {
            case null:
                img.classList.remove("d-block", "pull-left", "pull-right");
                break;
            case "block":
                img.classList.remove("pull-left", "pull-right");
                img.classList.add("d-block");
                break;
            case "left":
                img.classList.remove("d-block", "pull-left", "pull-right");
                img.classList.add("pull-left");
                break;

            case "right":
                img.classList.remove("d-block", "pull-left", "pull-right");
                img.classList.add("pull-right");
                break;
        }
    }

    const settings: any = useMemo(() => {

        let s: any = {
            inline: true,
            license_key: 'gpl',
            branding: false,
            promotion: false,
            icons: "lnauthoricons",
            language: lang,
            relative_urls: false,
            remove_script_host: false,
            // document_base_url: APP_URL,
            //theme: editorSettings && (editorSettings.theme || 'silver'),
            base_url: `/hugerte`,
            contextmenu: "imagealign,floatbox",
            plugins: (editorSettings?.plugins) || "media visualblocks code link autolink table charmap lists",
            toolbar: (editorSettings?.toolbar) || "undo redo | styles | table | bold italic removeformat | align | image floatbox media| bullist numlist outdent indent  |  link code charmap ",
            menubar: (editorSettings?.menubar) ?? false, // || "edit insert table format view tools",
            formats: {
                removeformat: [
                    {
                        selector: 'b,strong,em,i,font,u,strike,s,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins,small',
                        remove: 'all',
                        split: true,
                        block_expand: true,
                        expand: false,
                        deep: true
                    },
                    {
                        selector: 'h1,h2,h3,h4,h5',
                        remove: 'all',
                        split: true,
                        block_expand: true,
                        expand: false,
                        deep: true
                    },
                    { selector: 'span', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
                    { selector: 'img', attributes: ['style', 'class'], remove: 'none', split: true, expand: false, deep: true },
                    { selector: '*', attributes: ['style', 'class'], split: false, expand: false, deep: true }
                ]
            },
            toolbar_items_size: "small",
            browser_spellcheck: true,
            fixed_toolbar_container: editorSettings?.toolbar === "" ? "nope" : "#" + menuElementId,
            skin_url: `${PUBLIC_URL}/hugerte/skins/ui/oxide`,
            paste_as_text: true,
            valid_elements: (editorSettings?.valid_elements) || "@[contenteditable|id|style|data*],-h1,-h2,-h3,-h4,-h5,-strong/b,-em/i,#p," +
                "-div[class],br,table[class],tr[class],td[class],thead[class],tbody[class],th[class],tfoot[class],-span" +
                ",source,video[controls|width|height],iframe[width|height|src|frameborder|allowfullscreen],-ol,-ul,-li,dl,dt,hr,del/strike,ins,rp,rt,ruby,-small,sub,sup,-code,-blockquote" +
                ",-a[href|target=_blank],img[class|src|alt|title|width|height|align|data-id]",

            media_live_embeds: true,

            object_resizing: "img",
            table_default_attributes: {
                'class': 'table'
            },
            table_class_list: [
                { title: 'none', value: '' },
                { title: 'default', value: 'table' },
                { title: 'striped', value: 'table table-striped' },
                { title: 'condensed', value: 'table table-small' },
                { title: 'condensed_striped', value: 'table table-condensed table-striped' },
                { title: 'border', value: 'table table-bordered' }
            ],

            table_row_class_list: [
                { title: 'none', value: '' },
                { title: 'notification.displaytype.success', value: 'table-success' },
                { title: 'active', value: 'table-active' },
                { title: 'notification.displaytype.danger', value: 'table-danger' },
                { title: 'notification.displaytype.warning', value: 'table-warning' }
            ],

            table_cell_class_list: [
                { title: 'none', value: '' },
                { title: 'notification.displaytype.success', value: 'table-success' },
                { title: 'active', value: 'table-active' },
                { title: 'notification.displaytype.danger', value: 'table-danger' },
                { title: 'notification.displaytype.warning', value: 'table-warning' }
            ],

            //table_cell_class_list: [{ title: 'None', value: '' } ],

            table_row_advtab: true,
            table_cell_advtab: false,
            table_advtab: false,

            setup: (ed: HugeRteEditor) => {

                editorSettings?.customSetup && editorSettings.customSetup(ed);


                ed.on('focus', (e: any) => {
                    onFocus && onFocus(e.target.bodyElement);
                });


                if (onSetup) {
                    onSetup(ed);
                }
                ed.on("drop", (e) => {
                    uploadImage(e, ed);
                });
                ed.on("paste", (ev) => onPaste(ev, ed));

                ed.ui.registry.addButton('image', {
                    icon: 'image',
                    tooltip: 'Insert/edit image',
                    onAction: () => imageDialog(ed)

                    // stateSelector: 'img:not([data-mce-object],[data-mce-placeholder])'
                });


                ed.ui.registry.addButton('floatbox', {
                    icon: 'floatbox',
                    tooltip: 'Box',
                    onAction: () => floatBoxDialog(ed),
                });



                ed.ui.registry.addContextMenu('imagealign', {

                    update: (element: Element) => {
                        return !(element as HTMLImageElement).src ? ''
                            : [
                                {
                                    icon: 'noalign', type: "item", text: "", onAction: () => setImageClass(element, null)
                                },
                                {
                                    icon: "block", type: "item", text: "", onAction: () => setImageClass(element, "block")
                                },
                                {
                                    icon: "leftalign", type: "item", text: "", onAction: () => setImageClass(element, "left")
                                },
                                {
                                    icon: "rightalign", type: "item", text: "", onAction: () => setImageClass(element, "right")
                                }
                            ]
                    }
                });

                setEditor(ed);

            }
        }

        const tableSettings = ["table_class_list", "table_row_class_list", "table_cell_class_list"];

        tableSettings.forEach(key => {
            s[key].forEach((e: any) => {
                e.title = t.getText(e.title);
            });
        })

        if (editorSettings) {
            s = { ...s, ...editorSettings }
        }


        return s;
    }, [lang, menuElementId, editorSettings, t, onFocus, onSetup, imageDialog, floatBoxDialog]);

    if (readOnly) return <div className="" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialValue || "<div>&nbsp;</div>") }}></div>

    return (
        <>
            <div onClick={onClick} ref={editorWrapper} className={`${invalid ? "invalid" : ""} ${props.className}  position-relative`}>
                <div className="outerMceMenuWrapper">
                    <div className="innerMceMenuWrapper" id={menuElementId}></div>
                </div>

                <Editor
                    onBlur={localOnBlur}
                    onEditorChange={handleEditorChange}
                    initialValue={initialValue}
                    id={editorId + name}
                    hugerteScriptSrc={`/hugerte/hugerte.min.js`}
                    init={settings}
                />

            </div>

            <ImageDrop onCloseDialog={onCloseDropDialog} files={dropFiles} imagePreviewOptions={[{ name: "full", maxheight: 1200, maxwidth: 1200 }]}
                onImageReady={handleDroppedFileReady} accept={fileOptions.fileExtensions} languageService={t}
                maxsize={fileOptions.maxsize} />


        </>
    )

});

export default HtmlTextEditor;